import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { ApiTableDescriptionModal } from 'components/ApiTable/components/ApiTableDescriptionModal';
import { IntentVerification } from 'components/ApiTable/components/IntentVerification/IntentVerification';
import {
  ApiTableContext,
  useApiTableContextSelectors,
} from 'components/ApiTable/model/ApiTableModel';
import { TableView } from 'components/Table/components/TableView';
import { WarningBox } from 'components/WarningBox/WarningBox';
import { type JSX, useEffect } from 'react';
import { simpleTypeMemo } from 'utils/react/simpleTypeMemo';
import { withModelProvider } from 'utils/react/withModelProvider';
import { ApiTableAdvancedFilter } from './ApiTableAdvancedFilter';
/**
 * Props passed through from ApiTable to ApiTableView
 */
export interface ApiTableViewProps {
  borderless?: boolean;
  className?: string;
  hideTitle?: boolean;
  rowHeight?: number;
  tableClassName?: string;
}

type Props = ApiTableViewProps;

const BareApiTableView = (props: Props): JSX.Element => {
  const ctxProps = useApiTableContextSelectors({
    addRule: prop('intentVerifications', 'addRule'),
    advancedFilterOpen: prop('advancedFilter', 'open'),
    disableTableRowDetail: prop('disableTableRowDetail'),
    dispatch: prop('dispatch'),
    enableIntentEditor: prop('enableIntentEditor'),
    endpoint: prop('endpoint'),
    reports: prop('reports'),
    tableModel: prop('tableModel'),
    tableRowDetailContainer: prop('tableRowDetailContainer'),
  });

  if (process.env.NODE_ENV === 'development') {
    useEffect(() => {
      if (ctxProps.enableIntentEditor && props.borderless) {
        stumble(
          'Table design does not support intent editor in borderless mode.',
        );
      }
    }, [ctxProps.enableIntentEditor, props.borderless]);
  }

  return (
    <div className={classNames('flex flex-col', props.className)}>
      {ctxProps.endpoint.deprecated && (
        <WarningBox
          className='my-xl'
          description={
            'This table is scheduled to be removed from the IP Fabric User Interface in a forthcoming release.'
          }
        />
      )}
      <ApiTableDescriptionModal />
      {ctxProps.enableIntentEditor && <IntentVerification />}
      <TableView
        model={ctxProps.tableModel}
        borderless={props.borderless}
        className={classNames('flex-1', props.tableClassName)}
        disableTableRowDetail={ctxProps.disableTableRowDetail}
        headerAddon={ctxProps.advancedFilterOpen && <ApiTableAdvancedFilter />}
        hideTitle={props.hideTitle}
        rowHeight={props.rowHeight}
        tableRowDetailContainer={ctxProps.tableRowDetailContainer}
      />
    </div>
  );
};

export const ApiTableView = withModelProvider(ApiTableContext)(
  simpleTypeMemo(BareApiTableView),
);
